import React, { useEffect } from 'react';
import { DndContext, DndContextProps } from '@wix/patterns-dnd-kit-core';
import { useSortable } from '@wix/patterns-dnd-kit-sortable';

export interface TableRowTestWrapperProps
  extends Omit<React.HTMLAttributes<HTMLTableRowElement>, 'id'> {
  id: string | number;
  index: number;
  children: React.ReactElement<
    React.HTMLAttributes<HTMLTableRowElement>,
    React.JSXElementConstructor<React.HTMLAttributes<HTMLTableRowElement>>
  >;
}

interface TableTestWrapperProps {
  sortableId: string;
  children: React.ReactElement<DndContextProps, typeof DndContext>;
}

const dndContext: Record<
  string,
  {
    activeId: string | number | null;
    activeIndex: number | null;
    overId: string | number | null;
    overIndex: number | null;
    onDragStart?(event: {
      active: { id: string | number; data: any };
    }): unknown;
    onDragEnd?(event: {
      active: { id: string | number };
      over?: { id: string | number };
    }): unknown;
    onDragCancel?(): unknown;
  } | null
> = {};

export const TableTestWrapper = (props: TableTestWrapperProps) => {
  const { children, sortableId } = props;
  const {
    props: { onDragStart, onDragEnd, onDragCancel },
  } = children;

  useEffect(() => {
    dndContext[sortableId] = {
      onDragStart,
      onDragEnd: onDragEnd as any,
      onDragCancel: onDragCancel as any,
      activeId: null,
      activeIndex: null,
      overId: null,
      overIndex: null,
    };

    return () => {
      dndContext[sortableId] = null;
    };
  }, [onDragStart, onDragEnd, onDragCancel, sortableId]);

  return children;
};

export const TableRowTestWrapper = function _DraggableTableRow(
  props: TableRowTestWrapperProps,
) {
  const { children, index, id, ...rest } = props;
  const { type: DraggableTableRow, props: draggableTableRowProps } = children;
  const { data } = useSortable({ id });
  const containerId = data.sortable.containerId;

  return (
    <DraggableTableRow
      {...draggableTableRowProps}
      onPointerDown={() => {
        const dnd = dndContext[containerId];

        if (dnd) {
          dnd.activeId = id;
          dnd.activeIndex = index;

          dnd.onDragStart?.({
            active: {
              id: dnd.activeId,
              data: {
                current: {
                  sortable: {
                    containerId,
                    items: [],
                    index,
                  },
                },
              },
            },
          });
        }
      }}
      onPointerUp={() => {
        const dnd = dndContext[containerId];

        if (dnd) {
          dnd.overId = id;
          dnd.overIndex = index;

          dnd.onDragEnd?.({
            active: {
              id: dnd.activeId!,
            },
            over: {
              id: dnd.overId,
            },
          });
        }

        setTimeout(() => {
          dndContext[containerId] = null;
        });
      }}
      {...rest}
      style={{
        ...draggableTableRowProps.style,
        ...rest.style,
        transition: undefined,
      }}
    />
  );
};
